import * as utils from "shared/utils";

/**
 * A source standing for every text on one shelf. The server resolves it to
 * those texts' `book:` sources - see `src/shelfSources.js`.
 */
const SHELF_PREFIX = "shelf:";

export const isShelfSource = (source: string): boolean =>
  source.startsWith(SHELF_PREFIX);

/**
 * Get the emoji icon for a source category.
 */
export const getSourceEmoji = (source: string): string => {
  if (source.startsWith(SHELF_PREFIX)) return "\uD83D\uDCDA";
  if (source.startsWith("book:")) return "\uD83D\uDCD6";
  if (source.startsWith("url:")) return "\uD83C\uDF10";
  if (source.startsWith("chat")) return "\uD83D\uDCAC";
  if (source === "manual") return "\u270D\uFE0F";
  if (source.startsWith("import")) return "\uD83D\uDCE5";
  return "\uD83D\uDCC4";
};

/**
 * Get filter-friendly aliases for a source's category.
 * These allow users to type synonyms like "text" or "book" to match book sources.
 */
export const getSourceFilterAliases = (source: string): string[] => {
  if (source.startsWith(SHELF_PREFIX)) return ["shelf", "collection"];
  if (source.startsWith("book:")) return ["book", "text"];
  if (source.startsWith("url:")) return ["url", "webpage", "domain", "website"];
  if (source.startsWith("chat")) return ["chat"];
  if (source === "manual") return ["manual"];
  if (source.startsWith("import")) return ["import"];
  return [];
};

/**
 * Format a source string for display in the UI.
 * @param source - The raw source string (e.g. "book:abc123", "url:example.com", "chat:bot")
 * @param title - Optional title for book sources, or name for shelf sources (from API response)
 * @param author - Optional author for book sources
 * @returns Display-friendly string
 */
export const formatSourceDisplayName = (
  source: string,
  title?: string,
  author?: string
): string => {
  if (source.startsWith(SHELF_PREFIX)) {
    return title || "Unknown Shelf";
  }

  if (source.startsWith("book:")) {
    if (title) {
      return author ? `${title}, ${author}` : title;
    }
    return "Unknown Book";
  }

  if (source.startsWith("url:")) {
    return source.slice(4);
  }

  // Chat words are kept as two separate sources rather than one "chat"
  // source: words from Ami's messages that the learner didn't understand are
  // usually at a higher level than words from their own writing (learners can
  // often understand when reading what they can't yet write), so they may want
  // to practise the two groups differently.
  if (source === "chat:bot") {
    return "Chat (Ami)";
  }

  if (source === "chat:user") {
    return "Chat (my writing)";
  }

  if (source === "chat") {
    return "Chat";
  }

  if (source === "manual") {
    return "Manual";
  }

  if (source === "import") {
    return "Import";
  }

  if (source.startsWith("import:")) {
    return source.slice(7);
  }

  return source;
};

export interface ContextSourceLink {
  title: string;
  href: string;
}

/**
 * Where a word's context came from (a text, webpage, video or chat), as a
 * link back to it.
 */
export const linkForContext = (context: any): ContextSourceLink | undefined => {
  if (context.bookID?.title) {
    return {
      title: utils.elide(context.bookID.title, 40),
      href: "/library/" + context.bookID._id,
    };
  }
  if (context.URL) {
    return {
      title: utils.elide(utils.getDomainFromUrl(context.URL), 40),
      href: context.URL,
    };
  }
  if (context.youTubeID) {
    return {
      title: "YouTube",
      href: "https://www.youtube.com/watch?v=" + context.youTubeID,
    };
  }
  if (context.userChatID) {
    return {
      title: formatSourceDisplayName(context.source ?? "chat"),
      href: "/chat/" + context.userChatID,
    };
  }
  return undefined;
};
